import {
    Component, DoCheck, Optional,
    ErrorHandler, ViewChild, ViewContainerRef,
    Injector, Inject, PLATFORM_ID,
    AfterViewInit,
    HostBinding
} from '@angular/core';

import { HttpClient } from '@angular/common/http';

import { Location, isPlatformServer } from '@angular/common';
import {
    Shell, Logger, BaseComponent, UserAgent, RequestParams, Retry,
    Head, AppConfig, SkipModelInit, Cache, SiteWideAlert, OpenGraph,
    OpenGraphMetadata,
    RetryFailure
} from '../common';
import { UserService, BillingAlertParams } from '../user';

import { Router, NavigationStart, NavigationEnd } from '@angular/router';
import { ApiConfiguration, ApiAppConfig, AppApi, ApiUser } from '../api';
import { Angulartics2GoogleTagManager } from 'angulartics2/gtm';
import { AppErrorHandler } from './error-handler';

import * as express from '../common/express-ssr';

import { environment } from '@tytapp/environment';
import { PlayerComponent } from 'media-playback/player/player.component';
import { Playback, GoogleCastService } from '@tytapp/media-playback';
import { EventReplayer } from '@rezonant/preboot';
import { TransferState, makeStateKey, Title } from '@angular/platform-browser';
import { LoggerService } from '@tytapp/common/logger.service';
import { AirPlayService } from 'media-playback/airplay.service';
import { REQUEST, RESPONSE } from '@nguniversal/express-engine/tokens';
import Bugsnag from '@bugsnag/js';
import { MessageDialogComponent } from './message-dialog/message-dialog.component';
import { NotificationsService } from '@tytapp/notifications';
import { HostApi, NavigateMessage, PlatformCapabilitiesMessage } from './host-api';
import { filter } from 'rxjs/operators';

const APP_STATUS_KEY = makeStateKey('app-status');
const ICONS_2019 = [
    'appearance.svg',
    'candidates.svg',
    'contact_support.svg',
    'content.svg',
    'get_tyt_plus.svg',
    'hosts.svg',
    'log_in_with_another_account.svg',
    'log_out.svg',
    'login.svg',
    'member_faq.svg',
    'my_account_status.svg',
    'my_profile.svg',
    'newsletters.svg',
    'notifications.svg',
    'plus_membership.svg',
    'privacy_policy.svg',
    'profile.svg',
    'register.svg',
    'restore_purchase.svg',
    'revew_tyt_plus.svg',
    'shows.svg',
    'terms_of_service.svg',
    'topics.svg'
];

interface IpifyResult {
    ip: string;
}

export interface Award {
    franchiseName: string;
    logo: string;
    url?: string;
    name: string;
    subline: string;
    year: string;
}

@SkipModelInit()
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    providers: [Head]
})
export class AppComponent extends BaseComponent implements DoCheck, AfterViewInit {
    constructor(
        private injector: Injector,
        private shell: Shell,
        private userService: UserService,
        private router: Router,
        private location: Location,
        private apiConfig: ApiConfiguration,
        private transferState: TransferState,
        private googleCastService: GoogleCastService,
        private airplayService: AirPlayService,
        private notifications: NotificationsService,
        private opengraph: OpenGraph,

        @Inject(ErrorHandler)
        private errorHandler: AppErrorHandler,
        private angularticsGoogleTagManager: Angulartics2GoogleTagManager,
        private userAgent: UserAgent,

        private logger: LoggerService,
        private retry: Retry,

        public appConfig: AppConfig,
        private http: HttpClient,
        private playback: Playback,
        private eventReplayer: EventReplayer,
        private head: Head,
        private appApi: AppApi,
        private title: Title,

        @Optional()
        private requestParams: RequestParams,

        @Inject(PLATFORM_ID)
        private platformId,

        @Inject(REQUEST) @Optional()
        private request: express.ExpressRequest,

        private hostApi: HostApi
    ) {
        super();

        if (request) {
            this.apiConfig.globalHeaders['X-User-Agent'] = request.header('user-agent');
        }

        googleCastService.initializeSender();
        airplayService.initialize();

        let wasReplayed = false;

        // Ensure we always boot eventually
        if (typeof window !== 'undefined') {

            Bugsnag.addMetadata('Environment', 'side', 'client');

            setTimeout(() => {
                if (wasReplayed)
                    return;

                if (this.eventReplayer) {
                    this.logger.info("Replaying preboot events (timed out while waiting for first route)...");
                    this.eventReplayer.replayAll();
                    wasReplayed = true;
                }
            }, 500);
        } else {
            Bugsnag.addMetadata('Environment', 'side', 'server');
        }

        let initialPageView = true;
        this.router.events.subscribe(ev => {
            if (ev instanceof NavigationStart) {
                this.announce(`Loading...`, 2000);
                // Clear network error upon navigation

                this.head.clearCanonicalLink();

                this.networkErrorType = null;

                if (!isPlatformServer(this.platformId)) {
                    this.logger.info(`Navigating to: ${ev.url}`);
                }

            } else if (ev instanceof NavigationEnd) {

                if (typeof window !== 'undefined' && this.eventReplayer && !wasReplayed) {
                    this.logger.info("Replaying preboot events...");
                    this.eventReplayer.replayAll();
                    wasReplayed = true;
                }

                if (typeof window !== 'undefined') {
                    let hash = window.location.hash;
                    if (hash && window.location.pathname.includes('faq')) {
                        setTimeout(() => {
                            let element = document.getElementById(hash.slice(1));
                            if (element) {
                                let wrapper = document.documentElement;
                                let count = element.offsetTop + 256;
                                wrapper.scrollBy({ top: count, left: 0 });
                            }
                        }, 1000);
                    } else {
                        window.scrollTo({
                            top: 0,
                            //behavior: 'smooth'
                        });
                    }
                }

                // The GTM "History Change" trigger is ludicrously inaccurate,
                // sometimes skipping initial page views
                // (https://stackoverflow.com/questions/41655123/gtm-randomly-skips-initial-pageview-in-single-page-app)
                //
                // The solutions listed there do not work, they delay the page load and degrade
                // performance to an unacceptable event and do not fully solve the problem
                //
                // Adding in SSR to the equation makes it even worse! We should be able to track page views and
                // sessions even if the app never bootstraps since we are sending the HTML of the page to the browser.
                // So to solve this, we are moving to the standard Page View plus the custom event defined below.
                // Note that Angulartics already provides a *similar* custom event (stupidly and confusingly called "Page View"
                // which cannot be easily distinguished from the standard Page View in the GTM Debug panel)
                // but that one will fire on the initial page load, which is not acceptable for SSR loads.
                //
                // This event will only fire on page views which are fully handled by the SPA, so page views handled by
                // the browser will only be tracked once instead of twice.

                if (typeof window !== 'undefined') {
                    if (initialPageView) {
                        initialPageView = false;
                        console.log(`[--] Page View (Initial)`);
                    } else {
                        console.log(`[--] Page View (SPA): ${window.location.href}`);

                        this.angularticsGoogleTagManager.eventTrack(undefined, {
                            'event': 'Virtual Page View',
                            'content-name': ev.url,
                            'userId': this.user ? this.user.uuid : undefined
                        });

                        // GTM+GA+SPA is a recipe for disaster. Use GA directly, GTM will be used for all tags except GA.

                        if (typeof window['ga'] !== 'undefined') {
                            let ga = window['ga'];
                            ga('set', 'page', ev.url);
                            ga('send', 'pageview');
                            console.log(`[GA] Page View`);
                        }

                        // Facebook analytics

                        if (typeof window['xfbq'] !== 'undefined') {
                            let xfbq = window['xfbq'];
                            xfbq('track', "PageView");
                            console.log(`[FP] Page View`);
                        }
                    }
                }
            }
        });

        this.shell.titleChanged.subscribe(title => {
            this.announce(`New page: ${title}`);
        });

        this.markUserAgent();
    }

    announcementTimeout;
    announce(message: string, delay = 100) {
        this.politeAnnouncement = '';
        clearTimeout(this.announcementTimeout);
        this.announcementTimeout = setTimeout(() => {
            this.politeAnnouncement = message;
        }, delay);
    }

    devShowIcons: boolean = false;
    icons2019: string[] = ICONS_2019;
    private appConfigCache = Cache.shared<ApiAppConfig>('appconfig', 1000 * 60 * 5, 10);

    get environment() {
        return environment;
    }

    private _isDemo = null;
    get isDemo() {
        if (this._isDemo !== null)
            return this._isDemo;

        if (typeof window !== 'undefined') {
            this._isDemo = (window.localStorage['tyt.content_mode'] == 'demo');
        }

        return this._isDemo || false;
    }

    set isDemo(value: boolean) {
        this._isDemo = value;
        if (typeof window !== 'undefined') {
            window.localStorage['tyt.content_mode'] = value ? 'demo' : 'production';
            window.location.reload();
        }

        Bugsnag.addMetadata('Environment', 'isDemo', value ? 'true' : 'false');
    }

    doCheckCounter = 0;

    /**
     * BEACHBALL PROBLEM IN SAFARI? WE'VE GOT YOU COVERED! READ THIS!
     *
     * > Hello darkness my old friend....
     * > I've come to talk with you again
     * > Because a vision softly creeping
     * > Let it's seeds while I was sleeping...
     *
     * If we hit a massive amount of angular dirty checks,
     * we have likely hit an Angular change detection loop which will
     * crash the application. If this happens, engineer can set a breakpoint
     * on the setDebugPointHere line to diagnose the situation.
     */
    public ngDoCheck() {
        if (environment.production)
            return;

        if (!this._enableAngularChangeLoopDetection)
            return;

        this.doCheckCounter += 1;

        if (this.doCheckCounter > 30000) {
            if (this.doCheckCounter == 30000)
                this.logger.error(`Angular has reached a high amount of change detection cycles. This is indicative of a change detection loop.`);

            let setDebugPointHere = 1;

            // If you can't get the debugger open to set the breakpoint when the
            // issue occurs, comment out the following line.

            debugger;
        }
    }

    private _enableAngularChangeLoopDetection = false;

    private _noLayout: boolean = false;

    public isChrome: boolean = false;
    public get noLayout() {
        return this._noLayout;
    }

    public set noLayout(value: boolean) {
        this._noLayout = value;
    }

    public dialog: string = null;
    public menuOpen: boolean = false;
    public dialogShaken: boolean = false;
    public appName = 'web';
    public platform: PlatformCapabilitiesMessage;

    @ViewChild('player')
    player: PlayerComponent;

    @ViewChild('dialogAnchor', { read: ViewContainerRef, static: true })
    dialogAnchor: ViewContainerRef;

    back() {
        this.location.back();
    }

    hideDialog() {
        this.shell.hideDialog();
    }

    failedToFetchAppConfig: boolean = false;

    async fetchStatus() {

        // If we are on the client side and we have a localStorage copy of the app status, we'll get that loaded in
        // to unblock general operation of the app. Feature flag checks will still require this information to be refreshed

        if (typeof window !== 'undefined' && window.localStorage && window.localStorage['app_status']) {
            try {
                this.appConfig.appStatus = JSON.parse(window.localStorage['app_status']);
            } catch (e) {
                this.appConfig.appStatus = null;
            }
        }

        this.errorHandler.appStatus = this.appConfig.appStatus;

        let transferredStatus = this.transferState.get<ApiAppConfig>(APP_STATUS_KEY, null);
        let ssrCacheKey = `appstatus`;
        let ssrCachedStatus = null;
        let now = Date.now();
        let clientSide = typeof window !== 'undefined';
        let serverSide = !clientSide;

        if (serverSide && !transferredStatus) {
            // Try to use the global cache
            let entry = this.appConfigCache.getItem(ssrCacheKey);
            if (entry) {
                ssrCachedStatus = entry.value;

                if (entry.expiresAt > now) {
                    //this.logger.info('[API] Using cached appStatus');
                    transferredStatus = entry.value;
                }
            }
        }

        if (transferredStatus) {
            this.appConfig.appStatus = transferredStatus;
        } else {

            // Fetch from the API directly

            try {
                this.appConfig.appStatus = await this.retry.standard(() => this.appApi.getStatus('1.0', this.appName).toPromise(), () => {

                    // We failed to get the app status entirely, even after several retries.
                    // If we have a cached SSR status, let's use that.

                    if (ssrCachedStatus) {
                        this.appConfig.appStatus = ssrCachedStatus;
                        this.logger.warning('Recovered from appStatus API failure by using expired value from SSR shared cache');
                    }

                    if (!this.appConfig.appStatus) {
                        this.errorHandler.handleError(
                            {
                                errorType: 'network-error',
                                message: 'Failed to fetch app status! The API is probably down :-( API endpoint: ' + this.apiConfig.basePath
                            }
                        );
                    }
                });
            } catch (e) {
                if (e instanceof RetryFailure) {
                    this.failedToFetchAppConfig = true;
                    this.onNetworkError();
                } else {
                    this.logger.error(`Caught error while fetching API status: ${e}`);
                    return;
                }
            }

            if (serverSide && this.appConfig.appStatus)
                this.appConfigCache.insertItem(ssrCacheKey, this.appConfig.appStatus);

            if (isPlatformServer(this.platformId))
                this.transferState.set<ApiAppConfig>(APP_STATUS_KEY, this.appConfig.appStatus);
        }

        if (this.appConfig.appStatus) {
            // Make it possible to clear the app server's persistent shared caches via a setting
            // returned by appstatus

            let cacheClearTime = this.appConfig.appStatus.settings['cache_cleared_at'];
            if (cacheClearTime && parseInt(cacheClearTime) < Date.now()) {
                Cache.clearShared();
            }

            // Allow the server to disable app-server shared caches

            let wasEnabled = Cache.getSharedCachesEnabled();
            let shouldEnable = !this.appConfig.appStatus.features.includes('apps.web.disable_shared_caches');
            if (wasEnabled != shouldEnable) {
                Cache.setSharedCachesEnabled(shouldEnable);
                if (shouldEnable)
                    this.logger.info(`App-server shared caches are now enabled.`);
                else
                    this.logger.info(`App-server shared caches are now disabled.`);
            }
        }

        this.appConfig.fireAppStatusReady();

        if (typeof window !== 'undefined' && window.localStorage)
            window.localStorage['app_status'] = JSON.stringify(this.appConfig.appStatus);

        this.errorHandler.appStatus = this.appConfig.appStatus;

    }

    private closePodcast() {
        if (this.player)
            this.player.dismiss();
    }
    /**
     * Shut down loading processes, because the app is far along in the booting
     * process.
     */
    private shutdownLoader() {
        // Cancel loading the background video of the loading screen
        if (typeof window !== 'undefined') {
            clearTimeout(window['longLoadTimeout']);
        }
    }

    public dialogAnimation = 'forward';

    /**
     * Instantiate and show a dialog, closing any previous dialog.
     * @param componentCls
     * @param args
     */
    private async openDialog(componentCls, args, animation = 'forward'): Promise<void> {

        if (!componentCls) {
            throw new Error(`No dialog class passed`);
        }

        this.dialogAnimation = animation;

        if (this.dialogTransitionPromise) {
            await this.dialogTransitionPromise;
        }

        let dialogName = Shell.getNameForDialog(componentCls);
        if (!this._noPushState && typeof window !== 'undefined') {
            window.history.pushState({
                type: 'dialog',
                dialog: dialogName
            }, '');
        }
        this._noPushState = false;

        if (!this.dialogVisible) {

            this.initDialog(componentCls, args);
            this.edgeDialogResize();
            this.dialogHidden = false;
            setTimeout(() => this.dialogVisible = true);
            this.dialogTransitionPromise = null;
            return Promise.resolve();
        }

        return this.dialogTransitionPromise = new Promise<void>((resolve, reject) => {

            this.switchingDialog = true;
            this.switchingDialogBack = false;

            setTimeout(() => {
                this.switchingDialog = false;
                this.dialogAnchor.clear();
                this.initDialog(componentCls, args);
                this.edgeDialogResize();
                this.switchingDialogBack = true;

                setTimeout(() => {
                    this.dialogTransitionPromise = null;
                    resolve();
                }, 500);
            }, 500);
        });
    }

    private initDialog(componentCls, args) {

        let [cfr, injector] = this.shell.getDynamicDialogContext(componentCls);

        let dialogComponentFactory = cfr.resolveComponentFactory(componentCls);
        let dialogComponentRef = this.dialogAnchor.createComponent<any>(dialogComponentFactory);
        let init: Function = dialogComponentRef.instance.init;
        dialogComponentRef.instance.initArguments = args;
    }

    public switchingDialog: boolean = false;
    public switchingDialogBack: boolean = false;

    @HostBinding('class.dialog-visible')
    public dialogVisible: boolean = false;
    public dialogHidden: boolean = true;

    private dialogTransitionPromise: Promise<void>;

    /**
     * Close the current dialog
     */
    public async closeDialog(): Promise<void> {

        if (this.dialogTransitionPromise) {
            await this.dialogTransitionPromise;
        }

        if (!this.dialogVisible)
            return Promise.resolve();

        if (!this._noPushState && typeof window !== 'undefined') {
            window.history.pushState({}, '');
        }
        this._noPushState = false;

        return this.dialogTransitionPromise = new Promise<void>((resolve, reject) => {
            this.dialogVisible = false;
            setTimeout(() => {
                this.dialogAnchor.clear();
                this.dialogTransitionPromise = null;
                this.dialogHidden = true;
                resolve();

            }, 500);
        });
    }

    browser: string;
    os: string;

    markUserAgent() {
        this.browser = this.userAgent.browser;
        this.os = this.userAgent.os;

        //if (process.env.ENVIRONMENT != 'test')
        //  Logger.info(`Browser/OS identified as: ${this.browser}/${this.os}`);

        if (typeof document !== 'undefined') {
            document.documentElement.classList.add(`${this.browser}-browser`);
            document.documentElement.classList.add(`${this.os}-os`);
        }
    }

    private edgeDialogResize() {
        if (typeof document === 'undefined')
            return;

        if (this.browser != 'edge')
            return;

        let dialogEl = document.getElementById('dialog-host');
        this.logger.info(`Edge: Resizing dialog to calc(50% - ${dialogEl.offsetWidth / 2}px)`);
        dialogEl.style.left = `calc(50% - ${dialogEl.offsetWidth / 2}px)`;
    }

    @HostBinding('class.podcast-player-visible')
    get appPlayingAudio() {
        if (!this.player)
            return false;

        if (!this.player.media)
            return false;

        let session = this.player.getCurrentPlaybackSession();
        if (session && session.videoDisabled)
            return true;

        return (this.player.assetType == 'audio');
    }

    initKeys() {
        if (typeof document === 'undefined')
            return;

        document.addEventListener('keydown', ev => {
            if (this.dialogVisible) {
                if (ev.key == 'Escape') {
                    this.closeDialog();
                }
            }
        });
    }

    public year: number = 2017;
    private _noPushState: boolean = false;

    ngAfterViewInit() {
        if (this.player)
            this.playback.registerAppPlayer(this.player);
    }

    private _offline = false;

    get offline() {
        return this._offline;
    }

    set offline(value) {
        this._offline = value;

        if (value) {
            this.shell.addAlert({
                id: 'offline',
                type: 'warning',
                message: 'Network offline, check your Internet connection',
                internal: true,
                url: '/offline'
            });
        } else {
            this.shell.removeAlert('offline');
        }
    }
    get networkError() {
        return this.networkErrorType !== null;
    }

    networkErrorType: 'offline' | 'serverDown' | 'networkError' = null;
    awards: Award[];
    awardPairs: Award[][];
    alerts: SiteWideAlert[];
    opengraphMetadata: OpenGraphMetadata;

    get isServer() {
        return typeof window === 'undefined';
    }

    private _isOnboarding = false;

    get isOnboarding() {
        return this._isOnboarding;
    }

    set isOnboarding(value) {
        this.userService.onboarding = value;
    }

    async init() {

        let self = this;

        if (typeof window !== 'undefined') {
            setTimeout(() => {
                console.info(
                    `%c                                                                 `,
                    `
            font-size: 300%;
            padding: 4em 0 0 0;
            background-image: rgb(39, 39, 39);
            background-repeat: no-repeat;
            background-position: left;
            background-size: contain;
            color: #fff;
            background-image: url(${location.origin}/assets/tytcom.svg);`);

                console.log(`%cCaution! Don't get scammed.`, 'font-size: 38pt; margin-top: 8em; color: maroon');
                console.log('%cIf someone told you to come here and paste something, do not do it! They are trying to trick you into compromising your account.', 'font-size: 12pt');

                // HIRING CALLOUT  ///////////////////////////////

                console.log('');
                console.log('%cLooking for a job?', 'font-size: 20pt;');
                console.log('We\'re looking for frontend engineers to help us build the next iteration of TYT.com.');
                console.log('%cIf you are interested, visit https://tyt.com/careers to learn more and be sure to let us know you found this message!', 'font-weight: bold;');
                console.log('');

                // ANGULAR
                // console.log('We\'re looking for Software Engineers with experience with Angular Universal (v6+), Ruby on Rails experience a plus');
                // console.log('%cIf you are interested, please direct your resume to liam -at- tyt -dot- com', 'font-weight: bold;');

                //////////////////////////////////////////////////////

            }, 5 * 1000);

            this.opengraph.metadataChanged.subscribe(m => this.opengraphMetadata = m);
        }

        this.awards = [
            {
                logo: '/assets/webby-honoree-2021.png',
                franchiseName: 'The Damage Report',
                name: 'Social Media Presence',
                subline: 'Official Webby Honoree',
                year: '2021'
            },
            {
                logo: '/assets/shorty_logo.png',
                franchiseName: 'The Young Turks',
                name: 'Audience Honor for Live Event Coverage',
                subline: 'Shorty Awards',
                year: '2021'
            },
            {
                logo: '/assets/webby_logo.png',
                franchiseName: 'The Young Turks',
                name: 'News and Politics: Series and Channel',
                subline: 'Official Webby Honoree',
                year: '2019'
            },
            {
                logo: '/assets/shorty_logo.png',
                franchiseName: 'The Young Turks',
                name: 'Best in Overall YouTube Presence',
                subline: 'Audience Honor',
                year: '2017'
            },
            {
                logo: '/assets/webby_logo.png',
                franchiseName: 'The Young Turks',
                name: 'Best in Overall YouTube Presence',
                subline: 'Audience Honor',
                year: '2017'
            },
            {
                logo: '/assets/streamy_logo.png',
                franchiseName: 'The Young Turks',
                name: 'Best News & Cultural Channel',
                subline: null,
                year: '2017'
            },
            {
                logo: '/assets/aha_logo.png',
                franchiseName: 'The Young Turks',
                name: 'Good without a god',
                subline: 'Advocating Progressive Values and Equality for Humanists, Atheists, and Freethinkers',
                year: '2017'
            },
        ];

        this.awardPairs = [];
        let pair = [];
        for (let award of this.awards) {
            pair.push(award);
            if (pair.length >= 2) {
                this.awardPairs.push(pair);
                pair = [];
            }
        }

        if (typeof window !== 'undefined') {
            window['applicationEnvironment'] = environment;
        }

        if (pair.length > 0) {
            this.awardPairs.push(pair);
        }

        this.apiConfig.globalHeaders = {};

        if (this.request) {
            this.apiConfig.globalHeaders['X-User-Agent'] = this.request.header('user-agent');
            this.apiConfig.globalHeaders['X-TYT-On-Behalf-Of'] = this.request.ip;
            if (Zone.current.get('requestId'))
                this.apiConfig.globalHeaders['X-TYT-Trace'] = Zone.current.get('requestId');
        }

        this.apiConfig.globalQueryParams = {
            get is_demo() {
                return self.isDemo ? 1 : 0;
            }
        };

        if (!isPlatformServer(this.platformId))
            Logger.info(`Initial page: ${this.location.path()}`);

        if (typeof window !== 'undefined') {
            this.offline = !navigator.onLine;
            window.addEventListener("online", () => {
                this.offline = false;
            });

            window.addEventListener("offline", () => {
                this.offline = true;
            });
        }
        this.errorHandler.networkError.subscribe(async error => this.onNetworkError());

        if (typeof window !== 'undefined') {

            window.addEventListener('popstate', ev => {
                let state = ev.state;
                if (state && state.type == 'dialog') {
                    this._noPushState = true;
                    this.shell.showDialog(Shell.getDialogByName(state.dialog));
                } else if (state?.type === 'notifications') {
                    if (this.notifications.visible) {
                        this.notifications.visible = false;
                        window.history.pushState({}, '');
                    }
                } else {
                    if (this.dialogVisible) {
                        this._noPushState = true;
                        this.shell.hideDialog();
                    }
                }
            });
        }

        let envName = environment.name || 'development';

        this.year = new Date().getFullYear();

        if (!isPlatformServer(this.platformId) && environment.testing)
            Logger.info(`Environment Name: ${envName || '(unknown)'}`);

        this.subscribe(this.userService.onboardingChanged, value => {
            this._isOnboarding = value;
        });

        this.subscribe(this.shell.dialogChanged, value => {
            if (value == null)
                this.closeDialog();
            else
                this.openDialog(value[0], value[1], value[2]);

            //this.dialog = value;
        });


        this.subscribe(this.shell.noLayoutChanged, value => this.noLayout = value);
        this.subscribe(this.shell.isChromeChanged, value => this.isChrome = value);
        this.subscribe(this.shell.dialogShake, () => {
            this.dialogShaken = false;
            setTimeout(() => {
                this.dialogShaken = true;
                setTimeout(() => {
                    this.dialogShaken = false;
                }, 500);

            }, 10);
        });

        this.initKeys();


        if (!isPlatformServer(this.platformId) && environment.name != 'test')
            this.logger.info(`App Booted`);

        this.subscribe(this.userService.userChanged, user => {
            this.user = user;
        });

        this.userService.init();

        // ---

        this.shutdownLoader();

        let platformCaps = await this.hostApi.handshake();
        this.platform = platformCaps;

        if (platformCaps) {
            console.log(`Received platform handshake for '${platformCaps.platform}':`);
            console.dir(platformCaps);
            this.appName = `${platformCaps.platform}-web`;
        }

        this.hostApi.messageReceived.pipe(filter(x => x.type === 'navigate'))
            .subscribe((m: NavigateMessage) => this.router.navigateByUrl(m.url));

        await this.fetchStatus();

        // Add the site-wide alert if we have one

        if (this.appConfig.appStatus && this.appConfig.appStatus.settings && this.appConfig.appStatus.settings.site_alert_msg) {
            this.shell.addAlert({
                id: 'notice',
                type: this.appConfig.appStatus.settings.site_alert_type || 'info',
                message: this.appConfig.appStatus.settings.site_alert_msg,
                url: this.appConfig.appStatus.settings.site_alert_url,
                backgroundColor: this.appConfig.appStatus.settings.site_alert_background_color,
                textColor: this.appConfig.appStatus.settings.site_alert_text_color,
            });
        }

        this.handleServerError();

        // Make the alerts available, this is just to add spacers so that the site content moves down to match the additional alerts

        this.shell.alertsChanged.subscribe(alerts => this.alerts = alerts);

        // Unbounce Popups

        if (typeof window !== 'undefined' && window.location.pathname !== '/cast/receiver') {
            let disabled = await this.appConfig.featureEnabled('disable_unbounce_popups');
            if (!disabled) {
                this.userService.ready.then(() => {
                    setTimeout(() => {
                        if (this.userService.user)
                            return;

                        let scriptElement = document.createElement('script');
                        scriptElement.src = 'https://f25f6eae692848909b12eabf517233e8.js.ubembed.com';
                        scriptElement.async = true;

                        document.body.appendChild(scriptElement);
                    }, 1000);
                });
            }
        }
    }

    async onNetworkError() {
        if (this.offline) {
            this.networkErrorType = 'offline';
            return;
        }

        try {
            let data = await this.http.get<IpifyResult>('https://api.ipify.org?format=json').toPromise();
            if (data.ip) {
                this.networkErrorType = 'serverDown';
            }
        } catch (e) {
            this.networkErrorType = 'networkError';
            // Our assumption was right, network is down.
        }
    }

    isLive: boolean = false;

    signOut() {
        this.userService.completeLogout();
        this.userService.user = null;
        this.router.navigate(['home']);
    }

    private handleServerError() {
        if (!this.requestParams)
            return;

        let params = this.requestParams.params;

        if (params.server_error) {
            this.shell.showDialog(MessageDialogComponent, 'Error', params.server_error);
        }
    }

    user: ApiUser = null;

    devLogout() {
        this.userService.completeLogout();
        this.userService.user = null;
    }

    devForcePanic() {
        throw new Error("This panic was forced using the Developer Tools");
    }

    devShowPrivacyNotice() {
        this.userService.showPrivacyPolicyNotice(new Date('2021-11-15'));
    }

    devShowBillingAlerts(scenario: string) {
        let scenarios: Record<string, BillingAlertParams> = {
            visitor: {
                signedIn: false, entitled: false, fullyCancelled: false, gateway: null,
                hasMembership: false, isStaff: false, paymentStatus: null, period: null,
                renewsOn: null, cancelledAt: null
            },
            cancelling: {
                signedIn: true,
                entitled: true,
                fullyCancelled: false,
                cancelledAt: new Date(Date.now() - 1000 * 60 * 15),
                gateway: 'stripe',
                hasMembership: true,
                isStaff: false,
                paymentStatus: 'cancelling',
                period: 'monthly',
                renewsOn: new Date(Date.now() + 1000 * 60 * 60 * 24 * 5)
            },
            pastDue: {
                signedIn: true,
                entitled: true,
                fullyCancelled: false,
                cancelledAt: null,
                gateway: 'stripe',
                hasMembership: true,
                isStaff: false,
                paymentStatus: 'past_due',
                period: 'monthly',
                renewsOn: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5)
            },
            revoked: {
                signedIn: true,
                entitled: false,
                fullyCancelled: true,
                cancelledAt: null,
                gateway: 'stripe',
                hasMembership: true,
                isStaff: false,
                paymentStatus: 'expired',
                period: 'monthly',
                renewsOn: new Date(Date.now() - 1000 * 60 * 60 * 24 * 38)
            }
        };

        this.userService.applyBillingAlertsWithParams(scenarios[scenario]);
    }

    get production() {
        return environment.name === 'production';
    }

    addTestsNotifications(): void {
        this.notifications.add({
            id: 1,
            text: 'Notification Normal Style',
            description: "This is a notification with a big description, this must test the text-overflow to prevent the description text being bigger than the notification itself",
            timestamp: new Date(),
            url: `https://google.com`,
            open_in_new_tab: true,
            style: 'normal',
            image: 'https://picsum.photos/100/100?random=2',
            demoOnly: true
        });

        this.notifications.add({
            id: 2,
            text: 'Notification Attention Style',
            description: "We've updated our privacy policy. Click for details",
            timestamp: new Date(),
            icon: 'warning',
            url: `https://google.com`,
            style: "attention",
            image: 'https://picsum.photos/100/100?random=1',
            demoOnly: true
        });

        this.notifications.add({
            id: 3,
            text: 'Notification Attention Style',
            description: "We've updated our privacy policy. Click for details",
            timestamp: new Date(),
            url: `https://google.com`,
            style: "muted",
            image: 'https://picsum.photos/100/100?random=3',
            demoOnly: true
        });

        this.notifications.add({
            id: 4,
            text: 'Notification Attention Style 2',
            description: "We've updated our privacy policy. Click for details",
            timestamp: new Date(),
            icon: 'warning',
            url: `https://google.com`,
            category: 'error',
            demoOnly: true
        });

        this.notifications.add({
            id: 5,
            text: 'Notification Attention Style 3',
            description: "We've updated our privacy policy. Click for details",
            timestamp: new Date(),
            icon: 'settings',
            url: `https://google.com`,
            category: 'warning',
            image: 'https://picsum.photos/100/100?random=4',
            demoOnly: true
        });

        this.notifications.add({
            id: 6,
            text: 'Notification Attention Style 6',
            description: "We've updated our privacy policy. Click for details",
            timestamp: new Date(),
            icon: 'settings',
            url: `https://google.com`,
            style: 'muted',
            demoOnly: true
        });

        this.notifications.add({
            id: 7,
            text: 'Notification Attention Style 7',
            description: "We've updated our privacy policy. Click for details",
            timestamp: new Date(),
            icon: 'settings',
            url: `https://google.com`,
            category: 'warning',
            style: 'attention',
            demoOnly: true
        });

        this.notifications.add({
            id: 8,
            text: 'Notification Attention Style 6',
            description: "We've updated our privacy policy. Click for details",
            timestamp: new Date(),
            icon: 'settings',
            url: `https://google.com`,
            style: 'muted',
            demoOnly: true
        });

        this.notifications.add({
            id: 9,
            text: 'Notification Attention Style 7',
            description: "We've updated our privacy policy. Click for details",
            timestamp: new Date(),
            icon: 'settings',
            url: `https://google.com`,
            category: 'warning',
            style: 'attention',
            demoOnly: true
        });
    }

    get useBanta() {
        return environment.useBanta;
    }

    set useBanta(value) {
        environment.useBanta = value;
    }

    get isPreProduction() {
        return environment.name !== 'production';
    }

    overrideApi(mode: 'staging' | 'production' | '') {
        if (typeof window === 'undefined')
            return;

        localStorage.setItem('tyt-api-override', mode);
        window.location.reload();
    }

    get currentApiOverride() {
        if (typeof window === 'undefined')
            return '';

        return localStorage.getItem('tyt-api-override') ?? '';
    }

    skipToContent(event: Event) {
        console.log(`Skipping to content`);
        event.preventDefault();
        event.stopPropagation();

        let container: HTMLElement = document.querySelector('.x-main-content');
        if (!container)
            container = document.querySelector('#main-content');

        if (container) {
            container.focus();
            container.scrollIntoView();
        } else {
            console.warn(`Could not find main content to jump to!`);
        }
    }

    politeAnnouncement: string = '';
}
